/**
 * GuardTrail
 *
 * Renders a polyline on the map showing a guard's patrol path.
 * Uses Google Maps Polyline API directly since google-map-react doesn't have
 * a built-in Polyline component.
 *
 * @see Issue 7.2: Add location trail visualization
 */

import { useEffect, useRef } from 'react';
import { LocationHistoryPoint, TrailOptions, GuardStatus, TRAIL_COLORS } from './types';

interface GuardTrailProps {
  /** Array of location points forming the trail (oldest first) */
  trail: LocationHistoryPoint[];
  /** Whether the trail should be visible */
  isVisible: boolean;
  /** Guard's current status for color coding */
  guardStatus?: GuardStatus;
  /** Google Maps instance (from onGoogleApiLoaded) */
  map: google.maps.Map | null;
  /** Google Maps namespace (from onGoogleApiLoaded) */
  maps: typeof google.maps | null;
  /** Custom styling options */
  options?: TrailOptions;
}

/**
 * GuardTrail component manages a Google Maps Polyline for displaying
 * a guard's location history on the map.
 *
 * The component handles:
 * - Creating the polyline when map is available
 * - Updating the path when trail data changes
 * - Adding direction arrows to show travel direction
 * - Cleaning up the polyline on unmount
 */
const GuardTrail: React.FC<GuardTrailProps> = ({
  trail,
  isVisible,
  guardStatus = 'active',
  map,
  maps,
  options = {},
}) => {
  // Ref to store the polyline instance
  const polylineRef = useRef<google.maps.Polyline | null>(null);

  // Extract options with defaults
  const {
    strokeColor = TRAIL_COLORS[guardStatus],
    strokeOpacity = 0.8,
    strokeWeight = 3,
    showArrows = true,
  } = options;

  // Create/update/destroy polyline based on visibility and data
  useEffect(() => {
    // Can't draw without map instance
    if (!map || !maps) {
      return;
    }

    // Remove existing polyline if hiding or no trail
    if (!isVisible || trail.length < 2) {
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }
      return;
    }

    // Convert trail points to Google Maps LatLng path
    const path = trail.map(
      (point) => new maps.LatLng(point.latitude, point.longitude)
    );

    // Define arrow icon for direction indication
    const arrowIcon: google.maps.Symbol = {
      path: maps.SymbolPath.FORWARD_CLOSED_ARROW,
      scale: 3,
      strokeColor: strokeColor,
      strokeWeight: 2,
      fillColor: strokeColor,
      fillOpacity: 1,
    };

    // Calculate icon positions along the path
    // Show arrows at 25%, 50%, 75%, and 100% (end) of the path
    const icons: google.maps.IconSequence[] = showArrows
      ? [
          { icon: arrowIcon, offset: '25%' },
          { icon: arrowIcon, offset: '50%' },
          { icon: arrowIcon, offset: '75%' },
          { icon: arrowIcon, offset: '100%' },
        ]
      : [];

    // Create or update the polyline
    if (polylineRef.current) {
      // Update existing polyline
      polylineRef.current.setPath(path);
      polylineRef.current.setOptions({
        strokeColor,
        strokeOpacity,
        strokeWeight,
        icons,
      });
    } else {
      // Create new polyline
      polylineRef.current = new maps.Polyline({
        path,
        geodesic: true,
        strokeColor,
        strokeOpacity,
        strokeWeight,
        icons,
        map,
        zIndex: 5, // Below markers but above map features
      });
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }
    };
  }, [map, maps, trail, isVisible, strokeColor, strokeOpacity, strokeWeight, showArrows]);

  // This component doesn't render anything - it manages the polyline imperatively
  return null;
};

export default GuardTrail;
